import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { NameCardEntry } from './common';

interface NameCardState {
  cards: NameCardEntry[];
  // Millisecond timestamp written by the `video.live_stream.recording` mux
  // webhook. Used as a React `key` on the animated element so the CSS
  // animation runs exactly once when recording begins.
  triggerAt: number | null;
  hasBottomLeftWatermark: boolean;
}

const NameCardContext = createContext<NameCardState>({
  cards: [],
  triggerAt: null,
  hasBottomLeftWatermark: false,
});

export function useNameCardState(): NameCardState {
  return useContext(NameCardContext);
}

export function NameCardProvider({
  roomMetadata,
  hasBottomLeftWatermark,
  children,
}: {
  roomMetadata: string | undefined;
  hasBottomLeftWatermark: boolean;
  children: React.ReactNode;
}) {
  const [triggerAt, setTriggerAt] = useState<number | null>(null);
  const [cards, setCards] = useState<NameCardEntry[]>([]);

  useEffect(() => {
    if (!roomMetadata) return;
    let parsed: { name_cards?: { cards?: NameCardEntry[]; trigger_at?: number } };
    try {
      parsed = JSON.parse(roomMetadata);
    } catch {
      return;
    }
    const incomingTriggerAt = parsed.name_cards?.trigger_at;
    const incomingCards = parsed.name_cards?.cards ?? [];
    if (!incomingTriggerAt || incomingCards.length === 0) return;
    setCards(incomingCards);
    setTriggerAt(incomingTriggerAt);
  }, [roomMetadata]);

  const value = useMemo(
    () => ({
      cards,
      triggerAt,
      hasBottomLeftWatermark,
    }),
    [cards, triggerAt, hasBottomLeftWatermark]
  );
  return <NameCardContext.Provider value={value}>{children}</NameCardContext.Provider>;
}

// Renders the lower-third card for a single participant tile. Matches the
// participant by user_id parsed from LiveKit participant metadata. Staggers
// the start of each card by 2 seconds based on its index in the cards array
// (index 0 = host, no delay; index 1 = first guest, 2s delay; etc.).
export default function NameCardOverlay({ participantMetadata }: { participantMetadata: string | undefined }) {
  const { cards, triggerAt, hasBottomLeftWatermark } = useNameCardState();

  const userId = useMemo(() => {
    if (!participantMetadata) return null;
    try {
      const parsed = JSON.parse(participantMetadata) as { user_id?: number | string };
      if (parsed.user_id === undefined || parsed.user_id === null) return null;
      return typeof parsed.user_id === 'string' ? Number(parsed.user_id) : parsed.user_id;
    } catch {
      return null;
    }
  }, [participantMetadata]);

  if (triggerAt === null || userId === null || cards.length === 0) return null;
  const index = cards.findIndex((c) => c.user_id === userId);
  if (index < 0) return null;
  const card = cards[index];

  // Host fades in immediately when mux starts recording; guests stagger by 2s.
  const delayMs = index * 2000;
  const style: React.CSSProperties = { animationDelay: `${delayMs}ms` };

  const className = hasBottomLeftWatermark ? 'name-card name-card--above-watermark' : 'name-card';

  return (
    <div key={triggerAt} className={className} style={style}>
      <div className="name-card__name">{card.name}</div>
      {card.subtitle ? <div className="name-card__subtitle">{card.subtitle}</div> : null}
    </div>
  );
}
